import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsentLayout } from './ConsentLayout';
import {
  getConsentDraft,
  setConsentDraft,
} from '../../../lib/consent/consentDraft';

/**
 * Step 5 — parent-side identity verification.
 *
 * Wave 3.3: calls the existing `parental-verify` edge function
 * scaffold (Wave 1). The scaffold returns 503 with `{scaffold: true}`
 * until YOTI_API_KEY + YOTI_CLIENT_SDK_ID secrets are set.
 *
 * Behaviour:
 *   - Real Yoti integration (future Wave with commercial step):
 *     scaffold flag will be absent → response includes a sessionUrl
 *     to redirect the parent to.
 *   - Dev-bypass mode (current state, before Yoti is wired): scaffold
 *     flag is true → surface a clear "Yoti integration coming soon"
 *     banner + a Continue button that flips the verified flag in the
 *     consent draft + routes to Step 6. This lets the rest of the
 *     flow be testable end-to-end without a Yoti account.
 *
 * The dev-bypass is intentional + explicit. Wave 3.3 ships the surface;
 * the real Yoti redirect lands when YOTI_API_KEY is set and the
 * scaffold flag stops appearing.
 */

interface VerifyResponse {
  ok?: boolean;
  scaffold?: boolean;
  error?: string;
  sessionUrl?: string;
  sessionId?: string;
}

export function YotiRedirect() {
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'scaffold' | 'redirecting' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);

  // Guard: if Step 4 hasn't been completed, send back.
  useEffect(() => {
    const draft = getConsentDraft();
    if (!draft?.privacyNoticeRead) {
      navigate('/parent/consent/dob', { replace: true });
      return;
    }
    void callParentalVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function callParentalVerify() {
    const draft = getConsentDraft();
    if (!draft) return;

    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
    const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
    if (!supabaseUrl || !anonKey) {
      setState('error');
      setErrorMsg('Supabase env not configured.');
      return;
    }

    try {
      const resp = await fetch(`${supabaseUrl}/functions/v1/parental-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify({
          mode: 'kba',
          parent_email: draft.parentEmail ?? 'unknown@example.com',
          // Placeholder child id — the real child user_id doesn't exist
          // yet. The scaffold accepts any non-empty string ≥ 8 chars.
          child_user_id: draft.inviteToken ?? '00000000-0000-0000-0000-000000000000',
          consent_categories: ['account_creation'],
          return_url: `${window.location.origin}/parent/consent/consents`,
        }),
      });
      const data: VerifyResponse = await resp.json().catch(() => ({}));

      if (data.scaffold) {
        // Yoti not yet wired — dev-bypass path.
        setState('scaffold');
        return;
      }
      if (resp.ok && data.sessionUrl) {
        // Real Yoti — redirect.
        setSessionUrl(data.sessionUrl);
        setState('redirecting');
        // Auto-redirect after a moment so the parent sees what's happening.
        setTimeout(() => {
          window.location.assign(data.sessionUrl as string);
        }, 1500);
        return;
      }
      setState('error');
      setErrorMsg(data.error ?? `Verification couldn't start (${resp.status}).`);
    } catch (e) {
      setState('error');
      setErrorMsg((e as Error)?.message ?? 'Network error contacting verification.');
    }
  }

  const continueDevBypass = () => {
    setConsentDraft({});
    // Mark as verified in the draft so Step 7 commit accepts it.
    sessionStorage.setItem('4cm_consent_verified', 'true');
    navigate('/parent/consent/consents');
  };

  return (
    <ConsentLayout
      step={5}
      title="We need to know it's actually you"
      lead="UK law requires us to verify a parent's identity for an under-13 child. The check happens on a partner site (Yoti). 4CM only learns whether you're an adult — never your ID document or photo."
    >
      {state === 'loading' && (
        <div className="consent-status">
          <p className="consent-status__line">Setting up verification…</p>
        </div>
      )}

      {state === 'scaffold' && (
        <div className="consent-status consent-status--info">
          <h2 className="consent-status__heading">Yoti integration coming soon</h2>
          <p>
            We're finalising the commercial setup with Yoti. In the meantime,
            you can continue with the rest of the consent flow in dev mode —
            your consents will still be recorded and re-confirmed once Yoti
            is wired.
          </p>
          <div className="consent-form__actions">
            <button
              type="button"
              className="consent-form__btn"
              onClick={continueDevBypass}
            >
              Continue (dev mode)
            </button>
          </div>
        </div>
      )}

      {state === 'redirecting' && sessionUrl && (
        <div className="consent-status">
          <p className="consent-status__line">Redirecting you to Yoti…</p>
          <p className="consent-status__sub">
            If nothing happens, <a href={sessionUrl} className="consent-status__link">click here</a>.
          </p>
        </div>
      )}

      {state === 'error' && (
        <div className="consent-status consent-status--error">
          <h2 className="consent-status__heading">Couldn't start verification</h2>
          <p>{errorMsg ?? 'Something went wrong.'}</p>
          <div className="consent-form__actions">
            <button
              type="button"
              className="consent-form__btn consent-form__btn--ghost"
              onClick={() => void callParentalVerify()}
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </ConsentLayout>
  );
}
